import { cn } from "@/lib/utils";

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Animation variant
   * - 'shimmer': Gradient sweep animation (default, more polished)
   * - 'pulse': Simple opacity pulse (classic, lighter)
   */
  variant?: 'shimmer' | 'pulse';
  /**
   * Index for staggered animations (0-based)
   * Each subsequent skeleton delays by 100ms
   */
  index?: number;
}

function Skeleton({
  className,
  variant = 'shimmer',
  index = 0,
  style,
  ...props
}: SkeletonProps) {
  return (
    <div
      role="status"
      aria-label="Loading..."
      aria-busy="true"
      className={cn(
        "rounded-md bg-muted",
        variant === 'shimmer'
          ? "bg-gradient-to-r from-muted via-muted/50 to-muted bg-[length:200%_100%] animate-shimmer-gradient"
          : "animate-pulse",
        className
      )}
      style={{
        animationDelay: index > 0 ? `${index * 100}ms` : undefined,
        ...style,
      }}
      {...props}
    />
  );
}

// Preset skeleton components for common use cases
function SkeletonCard() {
  return (
    <div className="space-y-4 rounded-lg border bg-card p-6">
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
      <div className="mt-4 flex gap-2">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
      </div>
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-12 w-full" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="size-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonProduct() {
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <Skeleton className="h-48 w-full" />
      <div className="space-y-4 p-4">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        <div className="mt-4 flex items-center justify-between">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-10 w-28" />
        </div>
      </div>
    </div>
  );
}

// Dashboard skeleton for buyer/supplier/admin dashboards
function SkeletonDashboard() {
  return (
    <div className="space-y-6 duration-300 animate-in fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-4 rounded-lg border bg-card p-6">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="size-8 rounded" />
            </div>
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-3 w-32" />
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 rounded-lg border bg-card p-6 lg:col-span-2">
          <Skeleton className="h-6 w-40" />
          <SkeletonTable />
        </div>
        <div className="space-y-4 rounded-lg border bg-card p-6">
          <Skeleton className="h-6 w-32" />
          <SkeletonList />
        </div>
      </div>
    </div>
  );
}

// Page skeleton with header and content area
function SkeletonPage({ showHeader = true }: { showHeader?: boolean }) {
  return (
    <div className="space-y-6 duration-300 animate-in fade-in">
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-80" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
      )}
      <div className="rounded-lg border bg-card p-6">
        <SkeletonTable />
      </div>
    </div>
  );
}

// Sidebar skeleton
function SkeletonSidebar() {
  return (
    <div className="h-full space-y-6 p-4 duration-200 animate-in fade-in">
      {/* Logo */}
      <div className="flex items-center gap-4 px-2">
        <Skeleton className="size-10 rounded-lg" />
        <Skeleton className="h-6 w-28" />
      </div>

      {/* Nav items */}
      <div className="space-y-1">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-3 py-2">
            <Skeleton className="size-5" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>

      {/* Bottom section */}
      <div className="mt-auto space-y-1 border-t pt-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-3 py-2">
            <Skeleton className="size-5" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Full layout skeleton (sidebar + content)
function SkeletonLayout() {
  return (
    <div className="flex h-screen w-full bg-background">
      {/* Sidebar */}
      <aside className="hidden w-[280px] border-r bg-background md:flex">
        <SkeletonSidebar />
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top nav */}
        <header className="flex h-16 items-center justify-between border-b bg-background px-6">
          <Skeleton className="h-8 w-64" />
          <div className="flex items-center gap-4">
            <Skeleton className="size-10 rounded-full" />
            <Skeleton className="size-10 rounded-full" />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <SkeletonDashboard />
        </main>
      </div>
    </div>
  );
}

export {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonList,
  SkeletonProduct,
  SkeletonDashboard,
  SkeletonPage,
  SkeletonSidebar,
  SkeletonLayout
};
